import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState, useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { pdpFilterRef } from '@internal/backstage-plugin-pdp-filter-frontend';
import { getEnvironmentFromUrl } from '../utils/env';

// Module-level variables to ensure shared state across all hook instances
// DANGER: normally you wouldn't do something like this in a React app,
// but we don't need the reactivity of this loading state here, we really
// just want to prevent multiple calls to the API at the same time when using
// the hook in multiple components on the same page.
let globalIsLoading = false;

// Atoms for reactive state
const hrLeaderAtom = atom<boolean | null>(null);
const userIdAtom = atom<string | null>(null);

export const useUserInfo = () => {
  const [isHrLeader, setIsHrLeader] = useAtom(hrLeaderAtom);
  const [userId, setUserId] = useAtom(userIdAtom);
  const [error, setError] = useState<Error | undefined>();

  const identityApi = useApi(identityApiRef);
  const filterApi = useApi(pdpFilterRef);

  // Function to fetch user ID that can be reused
  const fetchUserId = useCallback(async () => {
    try {
      // Only fetch if not already fetched globally
      const identity = await identityApi.getBackstageIdentity();
      const userIdResult = identity.userEntityRef.split('/')[1];
      setUserId(userIdResult);
    } catch (_error) {
      setError(_error as Error);
    }
  }, [identityApi, setUserId]);

  // Function to fetch HR leader status that can be reused
  const fetchIsHrLeader = useCallback(async () => {
    // Otherwise start a new fetch
    try {
      globalIsLoading = true;
      let result: boolean = false;
      // If running in local environment, set HR leader to true
      if (getEnvironmentFromUrl(window.location.href) === 'local') {
        setIsHrLeader(true);
      } else {
        result = await filterApi.isHrLeader(userId);
        setIsHrLeader(result);
      }
    } catch (_error) {
      setError(_error as Error);
    } finally {
      globalIsLoading = false;
    }
  }, [filterApi, userId, setIsHrLeader]);

  // Effect to initialize userId
  useEffect(() => {
    if (!userId) {
      fetchUserId();
    }
  }, [userId, fetchUserId]);

  // Effect to fetch HR leader status
  useEffect(() => {
    if (isHrLeader === null && userId && !globalIsLoading) {
      fetchIsHrLeader();
    }
  }, [userId, isHrLeader, fetchIsHrLeader]);

  return {
    isHrLeader,
    userId,
    error,
    isLoading: globalIsLoading, // Always return the global loading state
  };
};
